
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Assessment, Category } from '@/types/assessment';
import { CategoriesTab } from './CategoriesTab';
import { QuestionsTab } from './QuestionsTab';
import { SettingsTab } from './SettingsTab';
import { SaveActions } from './SaveActions';

interface EditAssessmentTabsProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  title: string;
  setTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  slug: string;
  setSlug: (value: string) => void;
  formatSlug: (value: string) => string;
  isActive: boolean;
  setIsActive: (value: boolean) => void;
  isMaturityModel?: boolean;
  notifyEmails: string;
  setNotifyEmails: (value: string) => void;
  calendlyLink: string;
  setCalendlyLink: (value: string) => void;
  categories: Category[];
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  assessment?: Assessment | null;
  onContentChange?: () => void;
  loading?: boolean;
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  onPublish?: () => void;
}

export function EditAssessmentTabs({
  activeTab,
  setActiveTab,
  title,
  setTitle,
  description,
  setDescription,
  slug,
  setSlug,
  formatSlug,
  isActive,
  setIsActive,
  isMaturityModel,
  notifyEmails,
  setNotifyEmails,
  calendlyLink,
  setCalendlyLink,
  categories,
  setCategories,
  assessment,
  onContentChange,
  loading = false,
  hasUnsavedChanges = false,
  onSave,
  onPublish
}: EditAssessmentTabsProps) {
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        
        {onSave && (
          <SaveActions
            assessment={assessment}
            loading={loading}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={onSave}
            onPublish={onPublish}
            showPublish={activeTab !== 'settings'}
          />
        )}
      </div>
      
      <TabsContent value="categories" className="pt-0">
        <CategoriesTab 
          categories={categories}
          setCategories={setCategories}
          assessment={assessment}
        />
      </TabsContent>
      
      <TabsContent value="questions" className="pt-0">
        <QuestionsTab 
          categories={categories}
          setCategories={setCategories}
          isMaturityModel={isMaturityModel}
          assessment={assessment}
          onContentChange={onContentChange}
        />
      </TabsContent>
      
      <TabsContent value="settings" className="pt-0">
        <SettingsTab 
          title={title}
          setTitle={setTitle}
          description={description}
          setDescription={setDescription}
          slug={slug}
          setSlug={setSlug}
          formatSlug={formatSlug}
          isActive={isActive}
          onStatusChange={setIsActive}
          isMaturityModel={isMaturityModel}
          notifyEmails={notifyEmails}
          onNotifyEmailsChange={setNotifyEmails}
          calendlyLink={calendlyLink}
          onCalendlyLinkChange={setCalendlyLink}
          assessment={assessment}
        />
      </TabsContent>
    </Tabs>
  );
}
